import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { getCsatProvider } from '../services';

import collectiveApi from 'modules/common/collectiveApi';

type SubmitSurveyAnswersArgs = {
    surveyType: string;
    surveyId: string;
    surveyProperties: Record<string, unknown>;
    score: string | number;
    comment?: string;
    csatRowId: number;
};

const CSAT_RESPONSE_MAX_RETRIES = 1;

const isRetryableCsatResponseError = (error: unknown): boolean => {
    const status = (error as AxiosError)?.response?.status;
    if (status == null) {
        return true;
    }
    return status >= 500;
};

const submitSurvicateCsatResponse = async ({
    csatRowId,
    score,
    comment,
}: {
    csatRowId: number;
    score: string | number;
    comment?: string;
}) => {
    const response = await collectiveApi.post(`/surveys/csat/survey/response/`, {
        id: csatRowId,
        score: Number(score),
        comment: comment ?? '',
    });
    return response.data;
};

export const submitSurveyAnswers = async (args: SubmitSurveyAnswersArgs) => {
    const responseData = await submitSurvicateCsatResponse({
        csatRowId: args.csatRowId,
        score: args.score,
        comment: args.comment,
    });

    const provider = getCsatProvider();
    if (provider) {
        // Provider transport is fire-and-forget; backend POST above is authoritative.
        provider.submitAnswers({ score: args.score, comment: args.comment }).catch(() => {});
    }

    return responseData;
};

export default function useSubmitSurveyAnswers() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (args: SubmitSurveyAnswersArgs) => submitSurveyAnswers(args),
        retry: (failureCount, error) => {
            if (failureCount >= CSAT_RESPONSE_MAX_RETRIES) {
                return false;
            }
            return isRetryableCsatResponseError(error);
        },
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 2000),
        onSuccess: (data) => queryClient.setQueryData(['submitSurveyAnswers', data], data),
    });
}
